import { getTranslation } from '../i18n/translations';

// Status codes that should fall back to a status-keyed translation when no
// `error_code` is present on the response. Mirrors design.md D2.
const STATUS_FALLBACK_CODES = [401, 403, 404, 409, 422, 429];

const statusFallbackKey = (status) => `errors.http_${status}`;

/**
 * Resolve a user-facing message for an axios error response.
 *
 * Resolution order (design.md D2):
 *   1. If the response carries `error_code`, look up `errors.<code>` in the
 *      active locale. If a translation is found, return it with
 *      `fallback: false`.
 *   2. Otherwise, if the response status is one of the catalogued statuses,
 *      look up `errors.http_<status>` and return it with `fallback: true`.
 *   3. If neither resolves, return `null` so the caller can decide what to
 *      do (e.g. fall back to `detail`, leave silent, log, ...).
 *
 * @param {object} errorResponse  Axios error (or anything shaped like it)
 * @param {string} locale         Active locale: 'es' | 'en' | 'de'
 * @returns {{ key: string, text: string, fallback: boolean } | null}
 */
export function resolveErrorMessage(errorResponse, locale) {
  const code = errorResponse?.data?.error_code;
  const status = errorResponse?.status;

  if (code) {
    const key = `errors.${code}`;
    const text = getTranslation(locale, key);
    if (text && !text.startsWith('errors.')) {
      return { key, text, fallback: false };
    }
  }

  if (status && STATUS_FALLBACK_CODES.includes(status)) {
    const key = statusFallbackKey(status);
    const text = getTranslation(locale, key);
    if (text && !text.startsWith('errors.')) {
      return { key, text, fallback: true };
    }
  }

  return null;
}